<template>
  <el-dialog
    :title="title"
    width="520px"
    custom-class="agent-info-dialog"
    :visible="visible"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="handleClose"
    @closed="$emit('closed')"
  >
    <el-form
      ref="agentForm"
      class="agent-info-form"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <el-form-item label="经办人姓名：" prop="agentCertName">
        <el-input
          :value="form.agentCertName"
          maxlength="50"
          placeholder="请输入经办人姓名"
          @input="handleNameInput"
        />
      </el-form-item>
      <el-form-item label="经办人身份证号：" prop="agentCertNo">
        <el-input
          :value="form.agentCertNo"
          maxlength="30"
          placeholder="请输入18位身份证号"
          @input="handleCertNoInput"
        />
      </el-form-item>
      <div class="agent-tip">
        <i class="el-icon-warning-outline"></i>
        <span>经办人涉及到著作权人签章，请正确填写</span>
      </div>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleSubmit">保存</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { updateAgentInfoByDciUserId } from '@/api/user.js'
import {
  isSimpleIdCardNumber,
  normalizeAgentName,
  normalizeIdCardNumber
} from '@/utils/agentInfo.js'

export default {
  name: 'AgentInfoDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '填写经办人信息'
    },
    dciUserId: {
      type: String,
      default: ''
    },
    initialInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    const validateCertNo = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入经办人身份证号'))
        return
      }
      if (!isSimpleIdCardNumber(value)) {
        callback(new Error('请输入18位身份证号，最后一位为数字或X'))
        return
      }
      callback()
    }

    return {
      loading: false,
      form: {
        agentCertName: '',
        agentCertNo: ''
      },
      rules: {
        agentCertName: [
          { required: true, message: '请输入经办人姓名', trigger: 'blur' }
        ],
        agentCertNo: [
          { required: true, validator: validateCertNo, trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    visible (value) {
      if (value) {
        this.form = {
          agentCertName: normalizeAgentName(this.initialInfo.agentCertName),
          agentCertNo: normalizeIdCardNumber(this.initialInfo.agentCertNo)
        }
        this.$nextTick(() => {
          if (this.$refs.agentForm) this.$refs.agentForm.clearValidate()
        })
      }
    }
  },
  methods: {
    handleNameInput (value) {
      this.form.agentCertName = normalizeAgentName(value)
    },
    handleCertNoInput (value) {
      this.form.agentCertNo = normalizeIdCardNumber(value)
    },
    handleClose () {
      if (this.loading) return
      this.$emit('update:visible', false)
    },
    handleSubmit () {
      this.form.agentCertName = normalizeAgentName(this.form.agentCertName)
      this.form.agentCertNo = normalizeIdCardNumber(this.form.agentCertNo)
      this.$refs.agentForm.validate(async valid => {
        if (!valid) return
        if (!this.dciUserId) {
          this.$message.error('缺少著作权人用户ID，无法更新经办人信息')
          return
        }

        const submitData = {
          dciUserId: this.dciUserId,
          agentCertName: this.form.agentCertName,
          agentCertNo: this.form.agentCertNo
        }
        this.loading = true
        try {
          const res = await updateAgentInfoByDciUserId(submitData)
          if (!res || res.code !== 200) {
            this.$message.error((res && res.msg) || '经办人信息更新失败')
            return
          }
          this.$message.success('经办人信息更新成功')
          this.$emit('saved', submitData)
          this.$emit('update:visible', false)
        } catch (error) {
          this.$message.error('经办人信息更新失败，请稍后重试')
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .agent-info-dialog {
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 12px 36px rgba(31, 45, 61, 0.18);

  .el-dialog__header {
    padding: 20px 24px 18px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__title {
    color: #303133;
    font-size: 18px;
    font-weight: 600;
  }

  .el-dialog__headerbtn {
    top: 21px;
    right: 24px;
  }

  .el-dialog__body {
    padding: 24px 32px 18px;
  }

  .el-dialog__footer {
    padding: 16px 32px 20px;
    background-color: #f8fafc;
    border-top: 1px solid #ebeef5;
  }
}

.agent-info-form {
  padding: 0 8px;

  ::v-deep .el-form-item {
    margin-bottom: 20px;
  }

  ::v-deep .el-form-item__label {
    padding: 0 0 8px;
    color: #606266;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  ::v-deep .el-input__inner {
    height: 42px;
    padding: 0 14px;
    border-radius: 6px;
  }
}

.agent-tip {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 2px;
  padding: 11px 12px;
  color: #f56c6c;
  font-size: 13px;
  line-height: 20px;
  background-color: #fef0f0;
  border-radius: 6px;
  box-sizing: border-box;

  i {
    margin-top: 3px;
  }
}

.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;

  .el-button {
    min-width: 96px;
    height: 40px;
    margin: 0;
    border-radius: 6px;
  }
}
</style>
